import { getCurrentUser, isTeacherUser } from "../utils/auth";
import { fetchScopedCollections, toDate, parseIsoDate } from "../utils/scopeUtils";

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function endOfDay(date) {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    23,
    59,
    59,
    999
  );
}

function addDays(date, days) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function startOfWeek(date) {
  const day = (date.getDay() + 6) % 7; // Montag = 0
  return startOfDay(addDays(date, -day));
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function startOfYear(date) {
  return new Date(date.getFullYear(), 0, 1);
}

function isWithinRange(date, start, end) {
  return date >= start && date <= end;
}

function buildClampedDate(year, monthIndex, day, sourceTime) {
  const lastDayOfMonth = new Date(year, monthIndex + 1, 0).getDate();
  const safeDay = Math.min(day, lastDayOfMonth);

  return new Date(
    year,
    monthIndex,
    safeDay,
    sourceTime.getHours(),
    sourceTime.getMinutes(),
    sourceTime.getSeconds(),
    sourceTime.getMilliseconds()
  );
}

function getRangeBounds(range) {
  const now = new Date();

  if (range === "Heute") {
    const start = startOfDay(now);
    const end = now;
    const previousEnd = addDays(now, -1);
    const previousStart = startOfDay(previousEnd);
    return { start, end, previousStart, previousEnd };
  }

  if (range === "Wöchentlich") {
    const start = startOfWeek(now);
    const end = now;
    const previousEnd = addDays(now, -7);
    const previousStart = startOfWeek(previousEnd);
    return { start, end, previousStart, previousEnd };
  }

  if (range === "Monatlich") {
    const start = startOfMonth(now);
    const end = now;
    const previousEnd = buildClampedDate(
      now.getFullYear(),
      now.getMonth() - 1,
      now.getDate(),
      now
    );
    const previousStart = startOfMonth(previousEnd);
    return { start, end, previousStart, previousEnd };
  }

  const start = startOfYear(now);
  const end = now;
  const previousEnd = buildClampedDate(
    now.getFullYear() - 1,
    now.getMonth(),
    now.getDate(),
    now
  );
  const previousStart = startOfYear(previousEnd);

  return { start, end, previousStart, previousEnd };
}

function getUserName(user) {
  return user.displayName || user.name || user.email || "Unbekannter User";
}

function getStressLabel(level) {
  if (level >= 4) return "Belastet";
  if (level >= 3) return "Ruhig";
  return "Motiviert";
}

function buildRangeLabels(range) {
  if (range === "Heute") return ["08", "10", "12", "14", "16", "18", "20"];
  if (range === "Jährlich") {
    return ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];
  }
  return ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
}

function buildBuckets(range, start) {
  if (range === "Heute") {
    return Array.from({ length: 7 }, (_, i) => {
      const hour = 8 + i * 2;
      const from = new Date(
        start.getFullYear(),
        start.getMonth(),
        start.getDate(),
        hour,
        0,
        0,
        0
      );
      const to = new Date(
        start.getFullYear(),
        start.getMonth(),
        start.getDate(),
        hour + 1,
        59,
        59,
        999
      );
      return { label: String(hour).padStart(2, "0"), from, to };
    });
  }

  if (range === "Wöchentlich") {
    const labels = buildRangeLabels(range);

    return Array.from({ length: 7 }, (_, i) => {
      const from = startOfDay(addDays(start, i));
      const to = endOfDay(from);
      return { label: labels[i], from, to };
    });
  }

  if (range === "Monatlich") {
    const year = start.getFullYear();
    const month = start.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const buckets = [];

    for (let day = 1, week = 1; day <= daysInMonth; day += 7, week += 1) {
      const from = new Date(year, month, day, 0, 0, 0, 0);
      const to = new Date(
        year,
        month,
        Math.min(day + 6, daysInMonth),
        23,
        59,
        59,
        999
      );

      buckets.push({
        label: `W${week}`,
        from,
        to,
      });
    }

    return buckets;
  }

  const labels = buildRangeLabels(range);

  return Array.from({ length: 12 }, (_, i) => {
    const from = new Date(start.getFullYear(), i, 1, 0, 0, 0, 0);
    const to = new Date(start.getFullYear(), i + 1, 0, 23, 59, 59, 999);
    return { label: labels[i], from, to };
  });
}

function averageStress(logs) {
  if (!logs.length) return 0;
  const sum = logs.reduce((acc, item) => acc + Number(item.stressLevel || 0), 0);
  return Number((sum / logs.length).toFixed(1));
}

function buildLineSeries(range, currentItems, previousItems, selector) {
  const { start, previousStart } = getRangeBounds(range);
  const currentBuckets = buildBuckets(range, start);
  const previousBuckets = buildBuckets(range, previousStart);

  function bucketValue(bucket, items) {
    return selector(
      items.filter((item) => {
        const d =
          parseIsoDate(item.date) ||
          toDate(item.updatedAt) ||
          toDate(item.createdAt) ||
          toDate(item.finishedAt) ||
          toDate(item.lastActiveAt);

        return d && isWithinRange(d, bucket.from, bucket.to);
      })
    );
  }

  const maxLength = Math.max(currentBuckets.length, previousBuckets.length);

  return Array.from({ length: maxLength }, (_, index) => {
    const currentBucket = currentBuckets[index];
    const previousBucket = previousBuckets[index];

    return {
      label: currentBucket?.label || previousBucket?.label || `W${index + 1}`,
      value: currentBucket ? bucketValue(currentBucket, currentItems) : 0,
      value2: previousBucket ? bucketValue(previousBucket, previousItems) : 0,
    };
  });
}

export async function getStatsData(range = "Wöchentlich", selectedClass = "Alle") {
  const currentUser = getCurrentUser();
  const teacherMode = isTeacherUser(currentUser);
  const { start, end, previousStart, previousEnd } = getRangeBounds(range);

  const scopedCollections = await fetchScopedCollections();
  const users = scopedCollections.users;
  const tasks = scopedCollections.tasks;
  const stressLogs = scopedCollections.stressLogs;

  const classNames = Array.from(
    new Set(users.map((u) => u.className).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b, "de"));

  const effectiveSelectedClass =
    teacherMode && classNames.length === 1
      ? classNames[0]
      : selectedClass;

  const filteredUsers =
    effectiveSelectedClass === "Alle"
      ? users
      : users.filter((u) => u.className === effectiveSelectedClass);

  const allowedUserIds = new Set(filteredUsers.map((u) => u.id));

  const filteredTasks = tasks.filter((t) => allowedUserIds.has(t.userId));
  const filteredStress = stressLogs.filter((s) => allowedUserIds.has(s.userId));

  const currentStress = filteredStress.filter((item) => {
    const d =
      parseIsoDate(item.date) ||
      toDate(item.updatedAt) ||
      toDate(item.createdAt);
    return d && isWithinRange(d, start, end);
  });

  const previousStress = filteredStress.filter((item) => {
    const d =
      parseIsoDate(item.date) ||
      toDate(item.updatedAt) ||
      toDate(item.createdAt);
    return d && isWithinRange(d, previousStart, previousEnd);
  });

  const currentTasks = filteredTasks.filter((item) => {
    const d =
      parseIsoDate(item.date) ||
      toDate(item.updatedAt) ||
      toDate(item.createdAt);
    return d && isWithinRange(d, start, end);
  });

  const currentUsers = filteredUsers.filter((user) => {
    const d = toDate(user.lastActiveAt);
    return d && isWithinRange(d, start, end);
  });

  const avgStress = averageStress(currentStress);
  const elevatedCount = currentStress.filter(
    (item) => Number(item.stressLevel || 0) >= 4
  ).length;
  const totalStressCount = currentStress.length || 1;
  const elevatedPct = Math.round((elevatedCount / totalStressCount) * 100);
  const activeCount = currentUsers.length;
  const completionRate =
    currentTasks.length > 0
      ? Math.round(
          (currentTasks.filter((t) => t.done === true).length / currentTasks.length) * 100
        )
      : 0;

  const pieSlices = [
    {
      label: "Motiviert",
      value: currentStress.filter((s) => Number(s.stressLevel || 0) <= 2).length,
      color: "#AEDFF7",
    },
    {
      label: "Ruhig",
      value: currentStress.filter((s) => Number(s.stressLevel || 0) === 3).length,
      color: "#B8E986",
    },
    {
      label: "Belastet",
      value: currentStress.filter((s) => Number(s.stressLevel || 0) >= 4).length,
      color: "#FDBA74",
    },
  ].filter((slice) => slice.value > 0);

  const stressTrend = buildLineSeries(
    range,
    currentStress,
    previousStress,
    (items) => averageStress(items)
  );

  const activeTrend = buildLineSeries(
    range,
    filteredUsers,
    filteredUsers.filter((user) => {
      const d = toDate(user.lastActiveAt);
      return d && isWithinRange(d, previousStart, previousEnd);
    }),
    (items) =>
      items.filter((u) => {
        const d = toDate(u.lastActiveAt);
        return d != null;
      }).length
  );

  const classRows = classNames.map((className) => {
    const classUsers = users.filter((u) => u.className === className);
    const ids = new Set(classUsers.map((u) => u.id));

    const classStress = stressLogs.filter((s) => ids.has(s.userId));
    const classTasks = tasks.filter((t) => ids.has(t.userId));

    const classStressCurrent = classStress.filter((item) => {
      const d =
        parseIsoDate(item.date) ||
        toDate(item.updatedAt) ||
        toDate(item.createdAt);
      return d && isWithinRange(d, start, end);
    });

    const classTasksCurrent = classTasks.filter((item) => {
      const d =
        parseIsoDate(item.date) ||
        toDate(item.updatedAt) ||
        toDate(item.createdAt);
      return d && isWithinRange(d, start, end);
    });

    const avg = averageStress(classStressCurrent);
    const elevated = classStressCurrent.filter(
      (item) => Number(item.stressLevel || 0) >= 4
    ).length;
    const elevatedClassPct = classStressCurrent.length
      ? Math.round((elevated / classStressCurrent.length) * 100)
      : 0;

    const active = classUsers.filter((u) => {
      const d = toDate(u.lastActiveAt);
      return d && isWithinRange(d, start, end);
    }).length;

    let status = "Stabil";
    let tone = "ok";

    if (elevatedClassPct >= 50) {
      status = "Erhöhte Belastung";
      tone = "watch";
    } else if (elevatedClassPct >= 20) {
      status = "Beobachten";
      tone = "info";
    }

    return {
      id: className,
      klasse: className,
      avg,
      elevatedPct: elevatedClassPct,
      active: `${active} / ${classUsers.length}`,
      done: classTasksCurrent.filter((t) => t.done === true).length,
      status,
      tone,
    };
  });

  const userRows = filteredUsers.map((user) => {
    const userStress = currentStress.filter((s) => s.userId === user.id);
    const userTasks = currentTasks.filter((t) => t.userId === user.id);

    const userAvgStress = averageStress(userStress);

    return {
      id: user.id,
      name: getUserName(user),
      email: user.email || "—",
      avg: userAvgStress,
      status: getStressLabel(userAvgStress),
      active: toDate(user.lastActiveAt) ? "Aktiv" : "Keine Aktivität",
      done: userTasks.filter((t) => t.done === true).length,
    };
  });

  const bars =
    effectiveSelectedClass === "Alle"
      ? classRows.map((row) => ({
          label: row.klasse,
          value: row.avg,
        }))
      : userRows.map((row) => ({
          label: row.name.split(" ")[0] || row.name,
          value: row.avg,
        }));

  return {
    classes: classNames,
    total: filteredUsers.length,
    avgStress,
    elevatedCount,
    elevatedPct,
    activeCount,
    completionRate,
    stressTrend,
    activeTrend,
    pieSlices,
    classRows,
    userRows,
    bars,
  };
}